import React, { useState } from 'react';

import OwnerChip from './gantt_chart/OwnerChip';

import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import { useToast } from 'Hooks/useToast';
import { useUsers } from 'Hooks/useUsers';

const ownerSelectStyles = {
    menu: (base) => ({ ...base, minWidth: 220 }),
};

/**
 * The plan's owner, shown in the plan header: the initials chip and the name,
 * or a dash when nobody owns the plan. A user who can edit the plan picks a
 * new owner from the event's users; everyone else sees it read-only.
 */
const ProjectOwnerControl = ({ updateUrl, initialOwner = null, canEdit = false }) => {
    const [owner, setOwner] = useState(initialOwner);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    const { users } = useUsers();
    const toast = useToast();

    const save = (ownerId) => {
        setSaving(true);

        axios
            .put(updateUrl, { owner_id: ownerId ?? null })
            .then(({ data }) => {
                setOwner(data?.data?.owner ?? null);
                setEditing(false);
                toast.success($t('Owner updated successfully'));
            })
            .catch((error) => {
                toast.error(error.response?.data?.message || $t('Failed to update owner'));
            })
            .finally(() => setSaving(false));
    };

    const label = <span className="inbud-control-label !tw-mb-0">{$t('Plan owner')}</span>;

    if (editing) {
        return (
            <div className="tw-flex tw-items-center tw-gap-x-2">
                {label}
                <div className="tw-w-56">
                    <UserSelectorInput
                        selectedId={owner?.id ?? null}
                        selectedUser={owner ?? undefined}
                        usersList={users}
                        fetchIfNoUsers={false}
                        clearable
                        disabled={saving}
                        autoFocus
                        openMenuOnFocus
                        onChange={(value) => save(value ?? null)}
                        onBlur={() => !saving && setEditing(false)}
                        styles={ownerSelectStyles}
                    />
                </div>
            </div>
        );
    }

    const content = (
        <>
            <OwnerChip name={owner?.name} ownerId={owner?.id ?? null} />
            <span className="tw-text-sm tw-text-charcoal">{owner?.name || '—'}</span>
        </>
    );

    return (
        <div className="tw-flex tw-items-center tw-gap-x-2">
            {label}
            {canEdit ? (
                <button
                    type="button"
                    className="tw-flex tw-items-center tw-gap-x-2 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
                    title={$t('Change plan owner')}
                    onClick={() => setEditing(true)}
                >
                    {content}
                </button>
            ) : (
                <div className="tw-flex tw-items-center tw-gap-x-2">{content}</div>
            )}
        </div>
    );
};

export default ProjectOwnerControl;
